// Shared calculations for the SKU grid and inventory detail panel.
export function numeric(value: any): number | null {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : null;
}
export function usesDifferential(row: any): boolean {
  const hedged = row.Hedged ?? row.hedged;
  if (hedged === true || hedged === 1 || hedged === '1' || hedged === 'true' || Number(row.IsHedged) === 1) return true;
  if (Number(row.BuyingTermID) === 2) return true;
  if (hedged === false || hedged === 0 || hedged === '0' || Number(row.IsHedged) === 2) return false;
  // Older responses may omit hedge status; retain their fixed/diff convention.
  return !(numeric(row.agedwhseincost ?? row.AgedWhseInCost) ?? 0) && numeric(row.agedwhseindif ?? row.AgedWhseInDif) !== null;
}
export function costValue(row: any, kind: 'aged' | 'landed', diff: boolean): number | null {
  if (usesDifferential(row) !== diff) return null;
  const value = kind === 'aged'
    ? (diff ? row.agedwhseindif ?? row.AgedWhseInDif ?? row.AgedWhseInDiff : row.agedwhseincost ?? row.AgedWhseInCost)
    : row.LandedCost;
  const n = numeric(value);
  return diff || (n !== null && n > 0) ? n : null;
}
export function priceDisplay(diff: number | null, fixed: number | null): string {
  const parts: string[] = [];
  if (diff !== null) parts.push(`${diff < 0 ? '-' : '+'}${Math.abs(diff).toFixed(2)}`);
  if (fixed !== null) parts.push(`$${fixed.toFixed(2)}`);
  return parts.join(' / ') || '—';
}
export function summarizeCosts(rows: any[], quantity: 'AvailableQty' | 'ForwardQty', kind: 'aged' | 'landed') {
  const included = rows.filter(r => !r.PricingExcluded && (numeric(r[quantity]) ?? 0) > 0);
  const summarize = (diff: boolean) => {
    const matching = included.filter(r => usesDifferential(r) === diff);
    const values = matching.map(r => costValue(r, kind, diff)).filter((v): v is number => v !== null);
    return { bags: matching.reduce((n, r) => n + (numeric(r[quantity]) ?? 0), 0),
      average: values.length ? values.reduce((a,b) => a+b, 0) / values.length : null };
  };
  const fixed = summarize(false), diff = summarize(true);
  return { fixed, diff, count: included.length, bags: fixed.bags + diff.bags,
    display: priceDisplay(diff.average, fixed.average),
    detail: priceDisplay(diff.average, fixed.average) };
}
export function priceHistory(value: any): any[] {
  try { const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return (Array.isArray(parsed) ? parsed : parsed && typeof parsed === 'object' ? [parsed] : [])
      .filter(Boolean).map(r => ({...r, PriceDate: r.PriceDate ?? r.priceDate ?? r.price_date ?? null}))
      .sort((a,b) => (Date.parse(b.PriceDate) || 0) - (Date.parse(a.PriceDate) || 0));
  } catch { return []; }
}

// Contract labels include the market suffix (for example Mar27'C').
export function compareFuturesMonths(a: string, b: string): number {
  const key = (name: string) => {
    const match = String(name).match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[^0-9]*(\d{4}|\d{2})(?!\d)/i);
    if (!match) return Number.MAX_SAFE_INTEGER;
    const month = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'].indexOf(match[1].toLowerCase());
    const year = Number(match[2]) + (match[2].length === 2 ? 2000 : 0);
    return year * 12 + month;
  };
  return key(a) - key(b) || String(a).localeCompare(String(b));
}
